import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { makeStyles } from '@material-ui/core';
import { Money2, MoneyValue } from '~/modules/common/components';
import { CostOverrideType } from '../../CostOverrideType';
import { CostOverridePercentEditor } from './CostOverridePercentEditor';
import { CostOverrideTypeSelector } from './CostOverrideTypeSelector';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    width: '100%',
    '& .MuiFormControl-fullWidth': {
      width: '100% !important'
    }
  },
  valueEditor: {
    flex: 1,
    minWidth: theme.spacing(26),
    maxWidth: theme.spacing(40)
  },
  typeSelector: {
    flex: '0 0 auto',
    width: theme.spacing(20)
  },
  amountField: {
    '& input': {
      paddingTop: theme.spacing(1.75),
      paddingBottom: theme.spacing(1.75)
    },
    '& .MuiInputAdornment-marginDense': {
      paddingBottom: theme.spacing(1.75)
    }
  },
  amountReadOnlyField: {
    '&:focus': {
      outline: 'none',
      backgroundColor: theme.palette.grey[200]
    }
  }
}));

const formatPercent = value => `${value ?? 0}%`;

export const CostOverrideRateEditor = ({
  record,
  field,
  index,
  column: { onAmountChange, onCurrencyChange, onPercentChange, onTypeChange },
  readOnly
}) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const { amount, currency } = record[field] || {};
  const type = record.type ?? CostOverrideType.HOURLY_RATE;
  const percent = record.percent ?? 0;
  const isHourlyRate = type === CostOverrideType.HOURLY_RATE;

  const handleAmountChange = useCallback(
    e => {
      if (onAmountChange && e?.target && amount !== e.target.value) {
        onAmountChange(index, e.target.value || 0);
      }
    },
    [amount, index, onAmountChange]
  );

  const handleCurrencyChange = useCallback(
    value => {
      if (onCurrencyChange) {
        onCurrencyChange(index, value);
      }
    },
    [index, onCurrencyChange]
  );

  const handlePercentChange = useCallback(
    value => {
      if (onPercentChange) {
        onPercentChange(index, value);
      }
    },
    [index, onPercentChange]
  );

  const handleTypeChange = useCallback(
    nextType => {
      if (onTypeChange) {
        onTypeChange(index, nextType);
      }
    },
    [index, onTypeChange]
  );

  if (readOnly) {
    return isHourlyRate ? (
      <MoneyValue
        className={classes.amountReadOnlyField}
        money={record[field]}
        tabIndex={0}
      />
    ) : (
      <span className={classes.amountReadOnlyField}>
        {formatPercent(percent)}
      </span>
    );
  }

  return (
    <div className={classes.container}>
      <div className={classes.valueEditor}>
        {isHourlyRate ? (
          <Money2
            className={classes.amountField}
            onAmountChange={handleAmountChange}
            onCurrencyChange={handleCurrencyChange}
            amount={amount}
            currency={currency}
            disabled={record.disabled || record.isDimensionDeleted}
            currencyEditable={!record.disabled}
            ariaLabel={formatMessage({
              id: 'projectCostOverrides.type.hourlyRate'
            })}
            valueDataQeId="CostOverrideRateEditor_HourlyRate"
          />
        ) : (
          <CostOverridePercentEditor
            value={percent}
            onChange={handlePercentChange}
            disabled={record.disabled || record.isDimensionDeleted}
            dataQeId="CostOverrideRateEditor_PercentMarkup"
          />
        )}
      </div>
      <div className={classes.typeSelector}>
        <CostOverrideTypeSelector
          value={type}
          onChange={handleTypeChange}
          disabled={record.disabled || record.isDimensionDeleted}
          dataQeId="CostOverrideRateEditor_TypeSelector"
        />
      </div>
    </div>
  );
};

CostOverrideRateEditor.propTypes = {
  record: PropTypes.object.isRequired,
  field: PropTypes.string.isRequired,
  column: PropTypes.shape({
    onAmountChange: PropTypes.func,
    onCurrencyChange: PropTypes.func,
    onPercentChange: PropTypes.func,
    onTypeChange: PropTypes.func
  }).isRequired,
  index: PropTypes.number,
  readOnly: PropTypes.bool
};

export default CostOverrideRateEditor;
